import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { Subject, filter, take, of, tap } from 'rxjs';

/**
 * Feature Flag service.
 *
 * This service is used to lock down / hide features from applications that shouldn't be available for certain users.
 */
let FeatureFlagService = /*#__PURE__*/(() => {
  class FeatureFlagService {
    /**
     * Debug log observable
     */
    get log$() {
      return this.logSubject.asObservable();
    }
    constructor(http) {
      this.http = http;
      this.logSubject = new Subject();
      this.initializedSubject = new Subject();
      this.initialized = false;
      this.claims = [];
      this.baseUri = '';
      this.pendingChecks = [];
      this.cache = {};
      this.initPendingChecksHandler();
    }
    ngOnDestroy() {
      this.logSubject.complete();
      this.initializedSubject.complete();
      if (this.pendingChecks.length > 0) {
        this.pendingChecks.forEach(o => o.subject.complete());
      }
    }
    /**
     * Initializes the Feature flag service.
     */
    init() {
      this.log('Initializing Feature flag service...');
      this.baseUri = `${this.config?.baseUri}/FeatureFlags.BackEnd/FeatureFlags.WebAPI`;
      this.claims = this.formatClaims(this.config?.claims, this.config?.filterClaims);
      this.initializedSubject.next(true);
    }
    /**
     * Checks if a feature is available for current user.
     *
     * @param key Feature key
     * @returns True if user can access given feature, otherwise false.
     */
    isFeatureAvailable(key) {
      const body = {
        key,
        claims: this.claims
      };
      if (this.initialized) {
        return this.createCheckRequest(body);
      } else {
        const subject = new Subject();
        this.pendingChecks.push({
          body,
          subject
        });
        return subject.asObservable();
      }
    }
    initPendingChecksHandler() {
      this.initializedSubject.pipe(filter(result => result), take(1)).subscribe(initialized => {
        this.initialized = true;
        if (this.pendingChecks.length > 0) {
          this.pendingChecks.forEach(pending => {
            pending.body.claims = this.claims;
            this.createCheckRequest(pending.body).subscribe({
              next: result => {
                pending.subject.next(result);
                pending.subject.complete();
              },
              error: error => {
                pending.subject.error(error);
              }
            });
          });
          this.pendingChecks = [];
        }
      });
    }
    createCheckRequest(body) {
      if (this.config?.cacheableKeys?.includes(body.key) && body.key in this.cache) {
        return of(this.cache[body.key]);
      }
      return this.http.post(`${this.baseUri}/api/Check`, body, {
        headers: this.createHeaders()
      }).pipe(tap(result => {
        if (this.config?.cacheableKeys?.includes(body.key)) {
          this.cache[body.key] = result;
        }
      }));
    }
    log(content) {
      if (this.config?.debug) {
        this.logSubject.next(content);
      }
    }
    createHeaders() {
      return new HttpHeaders({
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      });
    }
    formatClaims(claims = {}, unusedClaims = []) {
      this.log('Claims from OAuth service:');
      this.log(claims);
      this.log('Claims to be filtered out:');
      this.log(unusedClaims);
      // Format claims into a format that is accepted by backend API
      let formattedClaims = [];
      for (const [claimKey, claim] of Object.entries(claims)) {
        if (Array.isArray(claim)) {
          claim.forEach(value => {
            formattedClaims.push({
              type: claimKey,
              value
            });
          });
        } else {
          formattedClaims.push({
            type: claimKey,
            value: claim
          });
        }
      }
      // Filter claims that shouldn't be included
      const filteredClaims = formattedClaims.filter(o => !unusedClaims.includes(o.type));
      return filteredClaims;
    }
    static {
      this.ɵfac = function FeatureFlagService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeatureFlagService)(i0.ɵɵinject(i1.HttpClient));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FeatureFlagService,
        factory: FeatureFlagService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FeatureFlagService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { FeatureFlagService };
